import React, { useEffect, useState } from "react";

import lodash from "lodash";

import { ParticipantTypes, Pseudonym, useDeletePseudonymMutation, useUpsertPseudonymMutation } from "@api/graphql";
import { PenWriteEditIcon } from "@assets/icons";
import { IconLink } from "@components/IconLink";
import { ToastMessage } from "@components/ToastMessage";
import { CustomAccordion } from "@components/accordion/CustomAccordion";
import DetailsRow from "@components/account_profile/DetailsRow";
import AddEditPseudonymModal from "@components/account_profile/account_details/AddEditPseudonymModal";

interface PseudonymsAccordionProps {
  participantTypes: (ParticipantTypes | undefined)[];
  pseudonyms?: (Pseudonym | undefined)[];
  participantId?: number;
  participantName?: string;
}

const PseudonymsAccordion: React.FC<PseudonymsAccordionProps> = ({
  participantTypes,
  pseudonyms,
  participantId,
  participantName,
}) => {
  const [showModal, setShowModal] = useState(false);
  const [currentPseudonym, setCurrentPseudonym] = useState<Pseudonym | null>(null);
  const [pseudonymList, setPseudonymList] = useState<Pseudonym[]>(lodash.compact(pseudonyms));
  const [upsertMutation] = useUpsertPseudonymMutation();
  const [deleteMutation] = useDeletePseudonymMutation();
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    setPseudonymList(lodash.compact(pseudonyms));
  }, [pseudonyms]);

  const shouldShow = participantTypes.includes(ParticipantTypes.ARTIST) && participantId !== undefined;

  if (!shouldShow) {
    return null;
  }

  const handleSubmit = async (inputPseudonym: string) => {
    if (!inputPseudonym || isSubmitting || !participantId) {
      return;
    }
    setIsSubmitting(true);

    try {
      const result = await upsertMutation({
        variables: {
          pseudonym: {
            pseudonym_id: currentPseudonym?.pseudonym_id ?? undefined,
            participant_id: participantId,
            pseudonym: inputPseudonym,
          },
        },
      });

      if (result.data?.upsertPseudonym?.success) {
        const updatedPseudonym: Pseudonym = {
          pseudonym_id: currentPseudonym?.pseudonym_id ?? result.data.upsertPseudonym.pseudonym_id,
          participant_id: participantId,
          pseudonym: inputPseudonym,
        };
        setCurrentPseudonym(updatedPseudonym);
        setPseudonymList((prevList) => {
          if (currentPseudonym && prevList.some((p) => p.pseudonym_id === currentPseudonym.pseudonym_id)) {
            return prevList.map((p) => (p.pseudonym_id === currentPseudonym.pseudonym_id ? updatedPseudonym : p));
          } else {
            return [...prevList, updatedPseudonym];
          }
        });
        setToastMessage("Pseudonym saved successfully!");
      } else {
        setToastMessage("Failed to upsert pseudonym.");
      }
    } catch (error) {
      setToastMessage("Error handling pseudonym.");
      console.error("Error:", error);
    } finally {
      setShowModal(false);
      setShowToast(true);
      setIsSubmitting(false);
    }
  };

  const handleDeletePseudonym = async (pseudonym: Pseudonym) => {
    if (!pseudonym || !participantId) {
      return;
    }

    try {
      const result = await deleteMutation({
        variables: {
          pseudonym: {
            pseudonym_id: pseudonym.pseudonym_id,
            participant_id: participantId,
            pseudonym: pseudonym.pseudonym,
          },
        },
      });

      if (result.data?.deletePseudonym?.success) {
        setPseudonymList((prevList) => prevList.filter((p) => p.pseudonym_id !== pseudonym.pseudonym_id));
        setToastMessage("Pseudonym deleted successfully!");
      } else {
        setToastMessage("Failed to delete pseudonym.");
      }
    } catch (error) {
      setToastMessage("Error deleting pseudonym.");
      console.error("Error:", error);
    } finally {
      setShowToast(true);
    }
  };

  const handleAddPseudonym = () => {
    setCurrentPseudonym(null);
    setShowModal(true);
  };

  const handleEditPseudonym = (pseudonym: Pseudonym) => {
    setCurrentPseudonym(pseudonym);
    setShowModal(true);
  };

  return (
    <>
      <ToastMessage message={toastMessage} show={showToast} setShow={setShowToast} type="info" />
      <CustomAccordion
        accordionKey="pseudonyms"
        contrastOpenStyle={false}
        header={<h5 className="mb-0">Pseudonyms</h5>}
        defaultActiveKey="pseudonyms"
        customHeaderStyle="py-4"
        preventCloseOnContentClick
        showBottomLine
      >
        <div className="mb-3">
          <IconLink src={PenWriteEditIcon} text="Add Pseudonym" onClick={handleAddPseudonym} />
        </div>
        <div className="d-flex flex-column">
          {pseudonymList.length > 0 &&
            pseudonymList.map((pseudonym, index) => (
              <DetailsRow
                key={pseudonym.pseudonym_id || index}
                details={[
                  {
                    title: `Pseudonym`,
                    value: pseudonym.pseudonym,
                  },
                  // Empty columns for UI alignment
                  {
                    title: ``,
                    value: <></>,
                  },
                  {
                    title: ``,
                    value: <></>,
                  },
                  {
                    title: ``,
                    value: <></>,
                  },
                  {
                    title: "",
                    value: (
                      <>
                        <IconLink src={PenWriteEditIcon} text="Edit" onClick={() => handleEditPseudonym(pseudonym)} />
                      </>
                    ),
                  },
                ]}
                className="mt-4"
              />
            ))}
        </div>
        {showModal && (
          <AddEditPseudonymModal
            show={showModal}
            onClose={() => setShowModal(false)}
            onSubmit={handleSubmit}
            onDelete={currentPseudonym ? () => handleDeletePseudonym(currentPseudonym) : undefined}
            pseudonym={currentPseudonym?.pseudonym || ""}
            participantName={participantName}
            isSubmitting={isSubmitting}
          />
        )}
      </CustomAccordion>
    </>
  );
};

export default PseudonymsAccordion;
